
import React from 'react';
import { SidebarContent } from '@/components/ui/sidebar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UserRole } from '@/types';
import SidebarNavGroup from './SidebarNavGroup';
import SidebarDropdownGroup from './SidebarDropdownGroup';
import { standaloneItems, lossControlItem, navigationGroups, adminItems, adminToolsGroup } from './navigationItems';

interface SidebarNavigationProps {
  userRole: UserRole;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({ userRole }) => {
  // Filter standalone items by role
  const filteredStandaloneItems = standaloneItems.filter(item => item.roles.includes(userRole));
  
  // Filter loss control item by role
  const filteredLossControlItems = lossControlItem.filter(item => item.roles.includes(userRole));
  
  // Filter navigation groups by role and their items
  const filteredNavigationGroups = navigationGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => item.roles.includes(userRole))
    }))
    .filter(group => group.roles.includes(userRole) && group.items.length > 0);
  
  // Find specific groups by title for proper ordering
  const maintenanceOpsGroup = filteredNavigationGroups.find(g => g.title === 'Maintenance Ops');
  const tenantGroup = filteredNavigationGroups.find(g => g.title === 'Tenant');
  const communicationHubGroup = filteredNavigationGroups.find(g => g.title === 'Communication Hub');
  const riskInsuranceGroup = filteredNavigationGroups.find(g => g.title === 'Risk & Insurance');
  const reportsAnalyticsGroup = filteredNavigationGroups.find(g => g.title === 'Reports & Analytics');
  
  // Filter admin items by role
  const filteredAdminItems = adminItems.filter(item => item.roles.includes(userRole));
  
  // Filter admin tools group by role
  const showAdminTools = adminToolsGroup.roles.includes(userRole);
  const filteredAdminToolsItems = adminToolsGroup.items.filter(item => item.roles.includes(userRole));

  return (
    <SidebarContent className="px-2 py-2">
      <ScrollArea className="h-full">
        <div className="pb-8 space-y-6">
          {/* Main Navigation */}
          <div className="space-y-1">
            {/* Standalone items (Operations Center) */}
            {filteredStandaloneItems.length > 0 && (
              <SidebarNavGroup
                title=""
                items={filteredStandaloneItems}
              />
            )}

            {/* Maintenance Ops group */}
            {maintenanceOpsGroup && (
              <SidebarDropdownGroup
                key={maintenanceOpsGroup.title}
                group={maintenanceOpsGroup}
              />
            )}

            {/* Tenant group */}
            {tenantGroup && (
              <SidebarDropdownGroup
                key={tenantGroup.title}
                group={tenantGroup}
              />
            )}

            {/* Communication Hub group */}
            {communicationHubGroup && (
              <SidebarDropdownGroup
                key={communicationHubGroup.title}
                group={communicationHubGroup}
              />
            )}

            {/* Compliance standalone item */}
            {filteredLossControlItems.length > 0 && (
              <SidebarNavGroup
                title=""
                items={filteredLossControlItems}
              />
            )}
          </div>

          {/* Secondary Navigation */}
          <div className="space-y-1 pt-4 border-t">
            {/* Risk & Insurance group */}
            {riskInsuranceGroup && (
              <SidebarDropdownGroup
                key={riskInsuranceGroup.title}
                group={riskInsuranceGroup}
              />
            )}
            
            {/* Reports & Analytics group */}
            {reportsAnalyticsGroup && (
              <SidebarDropdownGroup
                key={reportsAnalyticsGroup.title}
                group={reportsAnalyticsGroup}
              />
            )}
          </div>

          {/* Admin Tools Section */}
          {showAdminTools && filteredAdminToolsItems.length > 0 && (
            <div className="space-y-1 pt-4 border-t">
              <SidebarDropdownGroup
                group={{ ...adminToolsGroup, items: filteredAdminToolsItems }}
              />
            </div>
          )}

          {/* Settings Section */}
          {filteredAdminItems.length > 0 && (
            <div className="space-y-1 pt-4 border-t">
              <SidebarNavGroup
                title="Settings"
                items={filteredAdminItems}
              />
            </div>
          )}
        </div>
      </ScrollArea>
    </SidebarContent>
  );
};

export default SidebarNavigation;
